/* eslint-disable max-lines */
import {
    useContext, useEffect, useState
} from 'react';
import {
    Card, CardContent, CardFooter, CardTitle
} from 'shared/components/ui/card';
import {
    Label
} from 'shared/components/ui/label';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
    NestedPageLayout
} from 'shared/components/NewDesignComponents/PageLayout';
import {
    organizationIndex,
    subOrganizationService
} from 'shared/api';
import {
    Button
} from 'shared/components/ui/button';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from 'shared/components/ui/alert-dialog';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    Context
} from 'shared/context/GlobalState';
import AlertModal from 'shared/components/Alertmodal';
import {
    getOrganizationMember
} from 'shared/utils/organizationUtils';
import {
    formatDate
} from 'shared/utils/timeUtils';
import {
    Skeleton
} from 'shared/components/ui/skeleton';
import SubOrganizationMembers from 'features/organizations/subOrganization/SubOrganizationMembers';
import {
    RequireAccessComponent
} from 'shared/context/RequireAccess';
import {
    PERMISSIONS
} from 'shared/context/permissions';

const KbDetailRowSkeleton = ({
    valueWidth = 'w-32'
}) => (
    <div className='flex items-center justify-between gap-4 py-2.5'>
        <Skeleton className='h-4 w-20' />
        <Skeleton className={`h-4 ${valueWidth}`} />
    </div>
);

const VectorIndexSkeleton = () => (
    <div>
        <div className='divide-y divide-border'>
            <KbDetailRowSkeleton valueWidth='w-28' />
            <KbDetailRowSkeleton valueWidth='w-40' />
            <KbDetailRowSkeleton valueWidth='w-36' />
        </div>
        <div className='mt-6 flex w-full justify-end'>
            <Skeleton className='h-10 w-32 rounded-md' />
        </div>
    </div>
);

const MilvusCollectionSkeleton = () => (
    <div className='divide-y divide-border'>
        <KbDetailRowSkeleton valueWidth='w-24' />
    </div>
);

const SubOrganizationSettings = () => {

    const {
        selectedOrganization,
        getUserRole,
        subscription
    } = useContext(OrganizationContext);
    const {
        selectedSubOrganization,
    } = useContext(SubOrgContext);

    return (
        <NestedPageLayout>
            <div className='mx-auto flex w-full max-w-4xl flex-col gap-6'>
                <div>
                    <h1 className='text-2xl font-bold tracking-tight'>Sub-Organization Settings</h1>
                    <p className='text-sm text-muted-foreground'>Manage {selectedSubOrganization.name} — indexes and members.</p>
                </div>
                <Card className="w-full p-6">
                    <CardTitle className='mb-6'> Sub Organization </CardTitle>
                    <CardContent className='p-0'>
                        <dl className='grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2'>
                            <div className='flex flex-col gap-1'>
                                <Label className='text-gray-500'>Name</Label>
                                <span className='font-medium'>{selectedSubOrganization.name}</span>
                            </div>
                            <div className='flex flex-col gap-1'>
                                <Label className='text-gray-500'>Created</Label>
                                <span className='font-medium'>{new Date(selectedSubOrganization.created).toDateString()}</span>
                            </div>
                        </dl>
                    </CardContent>
                </Card>

                {/* sub organization vector index */}
                <SubOrganizationIndex
                    selectedOrganization={selectedOrganization}
                    getUserRole={getUserRole}
                />

                {subscription && (
                    <MilvusCollection
                        selectedOrganization={selectedOrganization}
                        getUserRole={getUserRole}
                    />
                )}

                {/* members card */}
                <RequireAccessComponent permission={PERMISSIONS.VIEW_ORGANIZATION_MEMBERS}>
                    <SubOrganizationMembers />
                </RequireAccessComponent>
            </div>
        </NestedPageLayout>
    );
};

const SubOrganizationIndex = ({
    selectedOrganization,
    getUserRole
}) => {

    const [createdByName, setCreatedByName] = useState('-');

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDelDialogOpen, setIsDelDialogOpen] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);

    const {
        addNewNotifcation
    } = useContext(Context);
    const {
        selectedSubOrganization, vectorIndex, kbConfigLoading, actions
    } = useContext(SubOrgContext);
    const roleInstance = getUserRole();

    const handleAddVectorIndex = () => {
        if (!roleInstance.isSuperAdmin) {
            return addNewNotifcation('You don not have the right access to activate vector index', 'warning');
        }

        setBtnLoading(true);
        const vectorIndexObj = {
            organizationId: selectedOrganization._id,
            subOrganizationId: selectedSubOrganization._id,
        };
        organizationIndex.createSubOrganizationVectorIndex(vectorIndexObj)
            .then((res) => {
                addNewNotifcation('Vector index activated successfully', 'success');
                setIsDialogOpen(false);
                actions({
                    type: 'SET_VECTOR_INDEX',
                    payload: res.data,
                });
            }).catch((error) => {

                if (error?.response?.status === 409) {
                    addNewNotifcation(error.response?.data?.message || 'Something went wrong. Please try again later', 'danger');
                } else {
                    addNewNotifcation('Something went wrong. Please try again later', 'danger');
                }
            }).finally(() => {
                setBtnLoading(false);
            });

    };

    const handleDelete = () => {

        if (!roleInstance.isSuperAdmin) {
            return addNewNotifcation('You don not have the right access to delete vector index', 'warning');
        }

        setBtnLoading(true);
        const vectorIndexObj = {
            organizationId: selectedOrganization._id,
            subOrganizationId: selectedSubOrganization._id,
        };
        organizationIndex.deleteSubOrganizationVectorIndex(vectorIndexObj)
            .then(() => {
                setIsDelDialogOpen(false);
                setIsDialogOpen(false);
                addNewNotifcation('Vector index deleted successfully', 'success');
                window.setTimeout(() => {
                    actions({
                        type: 'SET_VECTOR_INDEX',
                        payload: null
                    });
                }, 0);
            }).catch(() => {

                addNewNotifcation('Something went wrong. Please try again later', 'danger');
            }).finally(() => {
                setBtnLoading(false);
            });
    };

    useEffect(() => {
        if (kbConfigLoading) return;
        if (!vectorIndex) {
            setCreatedByName('-');
            setIsDelDialogOpen(false);
            setIsDialogOpen(false);
            return;
        }
        getOrganizationMember(vectorIndex?.createdBy, selectedOrganization)
            .then((name) => {
                setCreatedByName(name);
            }).catch(() => {

            });
    }, [vectorIndex, kbConfigLoading, selectedOrganization]);

    return (
        <Card className="w-full p-6">
            <CardTitle> MongoDB Vector Index </CardTitle>
            <CardContent className='mt-6 p-0'>
                <div className='flex flex-col'>
                    {kbConfigLoading ? (
                        <VectorIndexSkeleton />
                    ) : vectorIndex ? (
                        <div>
                            <dl className='divide-y divide-border'>
                                <div className='flex items-center justify-between gap-4 py-2.5'>
                                    <Label className='text-gray-500'>Name</Label>
                                    <span className='font-medium'>{vectorIndex.name}</span>
                                </div>
                                <div className='flex items-center justify-between gap-4 py-2.5'>
                                    <Label className='text-gray-500'>Created</Label>
                                    <span className='font-medium'>{formatDate(vectorIndex?.latestDefinitionVersion?.createdAt)}</span>
                                </div>
                                <div className='flex items-center justify-between gap-4 py-2.5'>
                                    <Label className='text-gray-500'>Created By</Label>
                                    <span className='font-medium'>{createdByName || '-'}</span>
                                </div>
                            </dl>

                            <div className='mt-6 flex w-full justify-end gap-2'>
                                <Button onClick={() => setIsDelDialogOpen(true)} disabled={!roleInstance.isSuperAdmin} variant='destructive'> Remove index </Button>

                                {/* delete vector index dialog alert */}
                                <AlertModal loading={btnLoading} openModal={isDelDialogOpen} setopenModal={setIsDelDialogOpen} onDelete={handleDelete} />
                            </div>
                        </div>
                    ) : (
                        <div className='flex flex-col w-full'>

                            <div className='flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-dashed py-10 text-center'>
                                <span className='max-w-md text-sm text-muted-foreground'> Activating the Vector Index will allow for faster and more efficient searches within this sub-organization. </span>
                                <Button
                                    disabled={!roleInstance.isSuperAdmin}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => setIsDialogOpen(true)}
                                >
                                    Activate Vector Index
                                </Button>
                            </div>

                            {/* create vector index dialog alert */}
                            <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Activate Vector Index</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            Are you sure you want to activate the Vector Index for <strong className='font-bold'> {selectedSubOrganization.name} </strong>?
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <div className="my-4 flex flex-col">
                                    </div>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <Button disabled={btnLoading} onClick={handleAddVectorIndex}>
                                            {btnLoading ? 'Activating...' : 'Activate'}
                                        </Button>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>

                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

const MilvusCollection = ({
    selectedOrganization,
    getUserRole
}) => {
    const [loading, setLoading] = useState(false);
    const [isDelDialogOpen, setIsDelDialogOpen] = useState(false);

    const {
        selectedSubOrganization,
        hasMilvusCollection,
        kbConfigLoading,
        actions
    } = useContext(SubOrgContext);
    const {
        addNewNotifcation
    } = useContext(Context);
    const roleInstance = getUserRole();

    const handleButtonClick = () => {
        if (hasMilvusCollection) {
            setIsDelDialogOpen(true);
        } else {
            handleActivateCollection();
        }
    };

    const handleActivateCollection = () => {
        if (!roleInstance.isSuperAdmin) {
            return;
        }

        setLoading(true);
        subOrganizationService.createMilvusCollection({
            organizationId: selectedOrganization._id,
            subOrganizationId: selectedSubOrganization._id
        })
            .then(() => {

                addNewNotifcation('Milvus collection created successfully', 'success');
                actions({
                    type: 'SET_MILVUS_COLLECTION_STATUS',
                    payload: true
                });
            }).catch(() => {

                addNewNotifcation('Failed to create Milvus collection', 'error');
            }).finally(() => {
                setLoading(false);
            });
    };

    const getButtonText = () => {
        if (hasMilvusCollection) {
            return 'Delete Milvus Collection';
        }
        return loading ? 'Creating...' : 'Create Milvus Collection';
    };

    const handleDelete = () => {
        // delete milvus collection logic here
        setLoading(true);
        subOrganizationService.deleteMilvusCollection({
            organizationId: selectedOrganization._id,
            subOrganizationId: selectedSubOrganization._id
        })
            .then(() => {

                setIsDelDialogOpen(false);
                addNewNotifcation('Milvus collection deleted successfully', 'success');
                actions({
                    type: 'SET_MILVUS_COLLECTION_STATUS',
                    payload: false
                });
            }).catch(() => {

                addNewNotifcation('Failed to delete Milvus collection', 'error');
            }).finally(() => {
                setLoading(false);
            });
    };

    return (
        <Card className="w-full p-6">
            <CardTitle> Milvus Collection </CardTitle>
            <CardContent className='mt-6 p-0'>
                {kbConfigLoading ? (
                    <MilvusCollectionSkeleton />
                ) : (
                <dl className='divide-y divide-border'>
                    <div className='flex items-center justify-between gap-4 py-2.5'>
                        <Label className='text-gray-500'>Status</Label>
                        <Badge variant={hasMilvusCollection ? 'default' : 'secondary'}>
                            {hasMilvusCollection ? 'Activated' : 'Not Activated'}
                        </Badge>
                    </div>
                </dl>
                )}
            </CardContent>
            <CardFooter className='mt-6 flex justify-end p-0'>
                {kbConfigLoading ? (
                    <Skeleton className='h-10 w-48 rounded-md' />
                ) : (
                <Button
                    onClick={handleButtonClick}
                    disabled={!roleInstance.isSuperAdmin || loading}
                    variant={hasMilvusCollection ? 'destructive' : 'default'}
                >
                    {getButtonText()}
                </Button>
                )}
            </CardFooter>
            <AlertModal
                loading={loading}
                openModal={isDelDialogOpen}
                setopenModal={setIsDelDialogOpen}
                onDelete={handleDelete}
            />

        </Card>
    );
};

export default SubOrganizationSettings;